// Pure pricing helpers ported from the old cart store. The backend's DiscountService decides the
// discount; these only turn `product.discount` / prices into numbers and labels for display.
import { retailSalesTax } from './country.js';

/** @typedef {import('$lib/types').Product & { cartCount?: number, discount?: any, best_discount?: any, all_discounts?: any[], test_kit_price?: any }} CartProduct */

/**
 * "Buy X get Y free" discounts carry no unit price.
 * @param {any} disco
 */
export function isVolumeFreeDiscount(disco) {
	return Boolean(disco && disco.volume_threshold && disco.additional_product);
}

/**
 * Unit price after a percentage / amount discount (volume-free discounts are ignored).
 * Test kits (product_type 2) use their own price.
 * @param {CartProduct} product
 * @returns {number}
 */
export function getStraightDiscountPrice(product) {
	let price = product.your_price.price;

	if (
		product.discount &&
		!isVolumeFreeDiscount(product.discount) &&
		product.discount.total_price
	) {
		price = product.discount.discounted_unit_price;
	}

	if (product.product_type == 2) {
		price =
			typeof product.test_kit_price !== 'undefined'
				? product.test_kit_price
				: product.your_price.price;
	}

	return parseFloat(String(price));
}

/**
 * The discount, unless it is a volume-free one.
 * @param {CartProduct} product
 */
export function getStraightDiscount(product) {
	if (product.discount && !isVolumeFreeDiscount(product.discount)) {
		return product.discount;
	}
	return undefined;
}

/**
 * Retail price including sales tax, as a 2dp string.
 * @param {CartProduct} product
 * @param {number} [salesTax]
 */
export function retailPrice(product, salesTax = retailSalesTax) {
	return parseFloat(String(product.retail_price.price * (1 + salesTax))).toFixed(2);
}

/**
 * Margin between wholesale and retail as a percentage string, or 0 when not applicable.
 * @param {CartProduct} product
 * @returns {string | number}
 */
export function retailMargin(product) {
	if (
		product.your_price.price > 0 &&
		product.retail_price.price > 0 &&
		product.your_price.price != product.retail_price.price
	) {
		return (
			((product.retail_price.price - product.your_price.price) / product.your_price.price) *
			100
		).toFixed(2);
	}
	return 0.0;
}

/**
 * @param {CartProduct} product
 * @param {number} [salesTax]
 */
export function lineRetailPrice(product, salesTax = retailSalesTax) {
	const count = parseInt(String(product.cartCount));
	return (product.retail_price.price * count * (1 + salesTax)).toFixed(2);
}

/**
 * Price for the whole line. Only in-stock units count for regular products; the backend supplies
 * `discount.total_price` when a discount applies.
 * @param {CartProduct} product
 * @returns {number}
 */
export function lineDiscountPrice(product) {
	const price = product.your_price.price;
	let count = parseInt(String(product.cartCount));

	if (product.product_type != 2 && product.product_type != 1) {
		if (product.stock && Number(product.cartCount) > Number(product.stock.stock_qty)) {
			count = parseInt(String(product.stock.stock_qty));
		}
	}

	let total;
	if (product.discount && product.discount.total_price) {
		total = product.discount.total_price;
	} else {
		total = price * count;
	}

	if (isNaN(price) || price <= 0) {
		console.error('Error: Invalid price for product ID:', product.id);
		return 0;
	}

	return parseFloat(total);
}

/** @param {CartProduct} product */
export function lineDiscount(product) {
	return product.discount;
}

/**
 * Number of free units in a "buy X get Y free" line.
 * @param {number} count
 * @param {number} threshold
 * @param {number} free
 */
export function calcFreeProducts(count, threshold, free) {
	if (count >= threshold) {
		return Math.floor(count / threshold) * free;
	}
	return 0;
}

/** @param {CartProduct} product */
export function lineDiscountDescription(product) {
	const disco = lineDiscount(product);
	if (!disco) return '';

	if (isVolumeFreeDiscount(disco)) {
		return (
			calcFreeProducts(
				Number(product.cartCount),
				disco.volume_threshold,
				disco.additional_product
			) + ' Free'
		);
	}
	const base = product.your_price.price * Number(product.cartCount);
	const amount = base - disco.total_price;
	return '$' + amount.toFixed(2) + ' Discount';
}

/** @param {CartProduct} product */
export function lineDiscountName(product) {
	const disco = lineDiscount(product);
	return disco ? disco.name : '';
}

/**
 * Best discount available to this customer on the product, whatever the quantity.
 * @param {CartProduct} product
 */
export function bestDiscountName(product) {
	if (product.best_discount === undefined || product.best_discount.name === undefined) {
		return '';
	}
	return product.best_discount.name;
}
